import { Box, Container } from '@material-ui/core'
import { Button, Grid, Typography } from '@mui/material'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import AddEditChapter from 'src/components/chapter/AddEditChapter'
import CommonDialog from 'src/components/common/CommonDialog'
import CommonStepper from 'src/components/common/commonStepper'
import CreateMoreBtn from 'src/components/common/createMoreBtn'
import DetailItem from 'src/components/common/detailItem'
import FilterSection from 'src/components/common/filterSection'
import PlayerComponent from 'src/components/helperComponents/PlayerComponent'
import useQueryTable from 'src/hooks/useQueryTable'
import { DeleteUnitById, GetUnits } from 'src/redux/actions'
import { courseDifficultyLevel } from 'src/redux/constants'
import { LocalTable } from 'src/ui/Table'
import { GetUnitTableColumns, StatusCell } from 'src/util/columns'
import { getFileUrl } from 'src/util/helpers'

import AddEditCourse from '../components/course/AddEditCourse'
import BulkUploadCourse, { downloadSampleCourseCsv } from '../components/course/BulkUploadCourse'

const steps = [
  'Create Course',
  'Create Chapter',
];

const UnitList = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [recordToEdit, setRecordToEdit] = useState()
  const [customFilterQuery, setCustomFilterQuery] = useState()
  const [searchText, setSearchText] = useState(null)
  const [activeStep, setActiveStep] = useState(0);
  const [firstStepData, setFirstStepData] = useState(null);
  const [mediaUrl, setMediaUrl] = useState();
  const [viewRecord, setViewRecord] = useState();
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const { data, isLoading, isFetching, refetch } = useQueryTable({
    name: 'units',
    searchText: searchText,
    pageIndex: pageIndex,
    pageSize: pageSize,
    additionalFilters: customFilterQuery,
  })

  // filter course list by courseId
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const lessonId = params.get("_id") || "";

    setCustomFilterQuery(prev => ({
      ...prev,
      _id: lessonId,
    }));
  }, [location.search]);

  // delete table list function
  const onDeleteRow = async recordToDelete => {
    if (recordToDelete) {
      await dispatch(DeleteUnitById(recordToDelete))
      refetch()
    }
  }

  // set course data for add chapter by action button
  useEffect(() => {
    if (recordToEdit) {
      setFirstStepData({
        languageId: recordToEdit?.course?._id,
        courseId: recordToEdit?._id,
        difficultyLevel: recordToEdit?.difficultyLevel,
      });
    }
  }, [recordToEdit]);

  const columns = useMemo(() => GetUnitTableColumns(setMediaUrl), []);

  const actionTitle = Object.keys(recordToEdit || {}).length === 0 ? "create-course" : "";

  return (
    <>
      <Helmet>
        <title>Course | Langistan</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3,
        }}
      >
        <Container maxWidth={false}>

          {/* addButton filters & search section  */}
          <FilterSection
            type="Course"
            path="courses"
            refetch={refetch}
            dispatcher={GetUnits}
            setSearchText={setSearchText}
            setRecordToEdit={setRecordToEdit}
            setCustomFilterQuery={setCustomFilterQuery}
            secondaryButton={
              <Button variant="outlined" color="primary" onClick={() => setIsBulkUploadOpen(true)}>
                + Bulk Upload
              </Button>
            }
          />

          {/* Course Table Data List */}
          <Box sx={{ pt: 3 }}>
            <LocalTable
              columns={columns}
              toolbarExtra={
                <Button variant="text" size="small" onClick={downloadSampleCourseCsv}>
                  Download Sample CSV
                </Button>
              }
              onEditClick={setRecordToEdit}
              onDeleteClick={onDeleteRow}
              additionalFilters={customFilterQuery}
              data={data}
              name="Course"
              exportApiName="units"
              exportFileName="courses.csv"
              isLoading={isLoading}
              isFetching={isFetching}
              setPagination={setPagination}
              addChildActionBtnTitle="Create Chapter"
              addChildActionBtnClick={() => {
                setActiveStep(1);
              }}
              viewChildActionBtnTitle="View Chapter"
              viewChildActionBtnUri={`/app/chapters?courseId=`}
              viewActionBtnTitle="View Course"
              onViewActionBtnClick={setViewRecord}
              editActionBtnTitle="Edit Course"
              deleteActionBtnTitle="Delete Course"
            />
          </Box>

          {mediaUrl && (
            <PlayerComponent
              url={mediaUrl}
              onDialogCancel={() => setMediaUrl()}
            />
          )}

          {/* View course details modal */}
          <CommonDialog
            open={!!viewRecord}
            title="Course Details"
            onClose={() => setViewRecord(null)}
            maxWidth="sm"
          >
            <Grid container spacing={2}>
              <DetailItem label="Language" value={viewRecord?.course?.name} />
              <DetailItem label="Course Name" value={viewRecord?.name} />
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                >
                  Description
                </Typography>
                <Typography variant="body1">{viewRecord?.description || 'N/A'}</Typography>
              </Grid>
              <DetailItem
                label="Difficulty Level"
                value={courseDifficultyLevel.find(d => d.key === viewRecord?.difficultyLevel)?.value}
              />
              <DetailItem label="Sort Order" value={viewRecord?.sortOrder} />
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                >
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StatusCell
                    value={
                      viewRecord?.isActive === false || viewRecord?.isActive === 'false'
                        ? 'InActive'
                        : 'Active'
                    }
                  />
                </Box>
              </Grid>
              <DetailItem
                label="Visibility"
                value={viewRecord?.unPublished ? 'For Admin Only' : 'For Students'}
              />
              <DetailItem
                label="Duration"
                value={`${viewRecord?.hrs || '00'}h ${viewRecord?.minutes || '00'}m`}
              />
              <DetailItem
                label="Created On"
                value={viewRecord?.createdAt ? moment(viewRecord.createdAt).format('DD/MM/YYYY') : null}
              />
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                >
                  Thumbnail
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {viewRecord?.videoThumbnail ? (
                    <img
                      src={getFileUrl(viewRecord.videoThumbnail)}
                      alt="course thumbnail"
                      style={{ width: 120, height: 70, borderRadius: 6, objectFit: 'cover' }}
                    />
                  ) : (
                    <Typography variant="body1">N/A</Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                >
                  Course Details Thumbnail
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {viewRecord?.courseDetailsThumbnail ? (
                    <img
                      src={getFileUrl(viewRecord.courseDetailsThumbnail)}
                      alt="course details thumbnail"
                      style={{ width: 120, height: 70, borderRadius: 6, objectFit: 'cover' }}
                    />
                  ) : (
                    <Typography variant="body1">N/A</Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                >
                  Video
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {viewRecord?.video ? (
                    <Button size="small" variant="outlined" onClick={() => setMediaUrl(viewRecord.video)}>
                      Play Video
                    </Button>
                  ) : (
                    <Typography variant="body1">N/A</Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CommonDialog>

          {/* Bulk upload courses via CSV */}
          <CommonDialog
            open={isBulkUploadOpen}
            title="Bulk Upload Courses"
            onClose={() => setIsBulkUploadOpen(false)}
            maxWidth="sm"
          >
            <BulkUploadCourse
              refetch={refetch}
              toggleDrawer={() => setIsBulkUploadOpen(false)}
            />
          </CommonDialog>

          {/* Add & Update course & chapter modal */}
          <CommonDialog
            open={!!recordToEdit}
            title={
              activeStep === 0
                ? `${actionTitle ? "Create" : "Update"} Course`
                : "Create Chapter"
            }
            onClose={() => {
              setRecordToEdit();
              setFirstStepData(null);
              setActiveStep(0);
            }}
            maxWidth="md"
          >
            {/*Stepper Box */}
            {actionTitle && <CommonStepper steps={steps} activeStep={activeStep} />}

            <Box mt={2}>
              {/* Add & Update course form */}
              {activeStep === 0 && (
                <AddEditCourse
                  selectedRow={recordToEdit}
                  refetch={refetch}
                  toggleDrawer={() => {
                    setRecordToEdit();
                    setFirstStepData(null);
                    setActiveStep(0);
                  }}
                  setFirstStepData={setFirstStepData}
                  setActiveStep={setActiveStep}
                  actionTitle={actionTitle}
                />
              )}

              {/* Add & Update chapter form */}
              {activeStep === 1 && (
                <AddEditChapter
                  toggleDrawer={() => {
                    setRecordToEdit();
                    setFirstStepData(null);
                    setActiveStep(0)
                  }}
                  refetch={refetch}
                  firstStepData={firstStepData}
                  setActiveStep={setActiveStep}
                  actionTitle={actionTitle}
                />
              )}

              {/* Add practice success message & add new practice button */}
              {activeStep === 2 && (
                <CreateMoreBtn
                  notification="Chapter created successfully!"
                  createMoreBtnTitle="+ Create more chapter"
                  onCreate={() => {
                    setActiveStep(1)
                  }}
                  onClose={() => {
                    setRecordToEdit()
                    setFirstStepData(null);
                    setActiveStep(0);
                  }}
                />
              )}
            </Box>
          </CommonDialog>
        </Container>
      </Box>
    </>
  )
}

export default UnitList
